import { useState, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { hideModal } from "actions/modal";
import { getCollections } from "selectors/mediaLibrarySelectors";
import { duplicateAssets, toggleSelectNone } from "actions/mediaLibrary";
import { isManagedLibraryCollection } from "../Utils";
import { MediaCollection } from "api/media-library";
import { CollectionSelectorMulti } from "features/media-library/components/collection-selector/collection-selector-multi";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

interface Asset {
  _id?: string;
  asset_id?: string;
  name?: string;
  fileName?: string;
}

interface DuplicateAssetsModalProps {
  assetIds: string[];
  currentCollection: MediaCollection;
  assets?: Asset[];
}

const EMPTY_ASSETS: Asset[] = [];

const DuplicateAssetsModal = ({
  assetIds,
  currentCollection,
  assets = EMPTY_ASSETS,
}: DuplicateAssetsModalProps) => {
  const dispatch = useDispatch();
  const collections = useSelector(getCollections);
  const [selectedCollectionIds, setSelectedCollectionIds] =
    useState<string[]>();
  const { isManagedCollection, platformName } =
    isManagedLibraryCollection(currentCollection);

  // for managed collections, filter out the current collection
  const filteredCollections = isManagedCollection
    ? collections.filter((collection) => collection.id !== currentCollection.id)
    : collections;

  const handleClose = useCallback(() => {
    dispatch(hideModal());
  }, [dispatch]);

  const handleCopy = useCallback(() => {
    dispatch(
      duplicateAssets(currentCollection._id, selectedCollectionIds, assetIds)
    );
    dispatch(toggleSelectNone());
  }, [dispatch, currentCollection._id, selectedCollectionIds, assetIds]);

  const isSingleAsset = assetIds.length === 1;
  const assetName = isSingleAsset
    ? assets[0]?.name || assets[0]?.fileName || "Untitled"
    : null;

  return (
    <Dialog open={true} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="max-w-[480px] p-0" showCloseButton={false}>
        <div className="flex flex-col">
          {/* Header */}
          <DialogHeader className="px-6 py-3">
            <DialogTitle className="font-gotham text-lg font-semibold text-neutral-900">
              Copy {isSingleAsset ? "Asset" : "Assets"}
            </DialogTitle>
            <DialogDescription className="font-inter text-sm text-neutral-600 mt-2">
              {isSingleAsset ? (
                <>
                  Choose a collection to copy &quot;
                  <strong className="text-neutral-900">{assetName}</strong>
                  &quot; to.
                </>
              ) : (
                <>Choose a collection to copy {assetIds.length} assets to.</>
              )}
            </DialogDescription>
          </DialogHeader>

          {/* Content */}
          <div className="px-6 pb-6 space-y-4">
            {/* Managed collection info */}
            {isManagedCollection && (
              <p className="font-inter text-sm text-neutral-600 leading-relaxed">
                After duplicating the assets to a Sked collection, you can
                easily edit them on Sked. Any customizations made to the
                duplicate will not affect the original in {platformName}.
              </p>
            )}

            {/* Collection selector */}
            <div className="space-y-1.5">
              <label className="font-gotham text-sm font-semibold text-neutral-700">
                Collections
              </label>
              <CollectionSelectorMulti
                collections={filteredCollections}
                selectedIds={selectedCollectionIds || []}
                onChange={(ids) => {
                  setSelectedCollectionIds(ids);
                }}
              />
            </div>
          </div>

          {/* Footer */}
          <DialogFooter className="flex flex-row items-center justify-between sm:justify-between border-t border-neutral-200 px-6 py-3">
            <Button
              variant="ghost"
              onClick={handleClose}
              className="text-neutral-600 hover:text-neutral-700 hover:bg-neutral-100"
            >
              Cancel
            </Button>
            <Button
              variant="blue-primary"
              disabled={
                !selectedCollectionIds || selectedCollectionIds.length === 0
              }
              onClick={handleCopy}
            >
              Copy
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default DuplicateAssetsModal;
